/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  Checkbox,
  Flex,
  Icon,
  Scrollbar,
  Spinner,
  Stack,
  Text,
} from '@tonic-one/react';
import { AngleDownIcon, AngleRightIcon } from '@tonic-one/react-icons';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect } from 'react';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import useTreeStyle from './style';
import { Key, TreeNode, TreeNodeProps, TreeProps } from './type';
import {
  getAllKeys,
  getCheckedKeys,
  isChecked,
  isIndeterminate,
  // traverseTreeData,
} from './utils';

export interface BasicDataNode {
  checkable?: boolean;
  disabled?: boolean;
  disableCheckbox?: boolean;
  icon?: IconType;
  isLeaf?: boolean;
  selectable?: boolean;
  switcherIcon?: IconType;
  /** Set style of TreeNode. This is not recommend if you don't have any force requirement */
  className?: string;
  style?: React.CSSProperties;
}
/** Provide a wrap type define for developer to wrap with customize fieldNames data type */
export type FieldDataNode<
  T,
  ChildFieldName extends string = 'children',
> = BasicDataNode &
  T &
  Partial<Record<ChildFieldName, FieldDataNode<T, ChildFieldName>[]>>;
export type DataNode = FieldDataNode<{
  key: string | number;
  title?: React.ReactNode | ((data: DataNode) => React.ReactNode);
}>;
export type IconType = React.ReactNode;
export type SwitcherIcon = React.ReactNode;
export type TreeLeafIcon = React.ReactNode;

export interface TreeNodePropsOrigin<
  TreeDataType extends BasicDataNode = DataNode,
> {
  eventKey?: Key;
  prefixCls?: string;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  expanded?: boolean;
  selected?: boolean;
  checked?: boolean;
  loaded?: boolean;
  loading?: boolean;
  halfChecked?: boolean;
  title?: React.ReactNode | ((data: TreeDataType) => React.ReactNode);
  dragOver?: boolean;
  dragOverGapTop?: boolean;
  dragOverGapBottom?: boolean;
  pos?: string;
  domRef?: React.Ref<HTMLDivElement>;
  /** New added in Tree for easy data access */
  data?: TreeDataType;
  isStart?: boolean[];
  isEnd?: boolean[];
  active?: boolean;
  onMouseMove?: React.MouseEventHandler<HTMLDivElement>;
  isLeaf?: boolean;
  checkable?: boolean;
  selectable?: boolean;
  disabled?: boolean;
  disableCheckbox?: boolean;
  icon?: IconType;
  switcherIcon?: IconType;
  children?: React.ReactNode;
}
export interface TreePropsOrigin {
  showLine?: boolean | { showLeafIcon: boolean | TreeLeafIcon };
  className?: string;
  /** Whether to support multiple selection */
  multiple?: boolean;
  /** Whether to automatically expand the parent node */
  autoExpandParent?: boolean;
  /** Node selection in Checkable state is fully controlled (the selected state of parent and child nodes is no longer associated) */
  checkStrictly?: boolean;
  /** Whether to support selection */
  checkable?: boolean;
  /** whether to disable the tree */
  disabled?: boolean;
  /** Expand all tree nodes by default */
  defaultExpandAll?: boolean;
  /** Expand the corresponding tree node by default */
  defaultExpandParent?: boolean;
  /** Expand the specified tree node by default */
  defaultExpandedKeys?: Key[];
  /** (Controlled) Expand the specified tree node */
  expandedKeys?: Key[];
  /** (Controlled) Tree node with checked checkbox */
  checkedKeys?: Key[] | { checked: Key[]; halfChecked: Key[] };
  /** Tree node with checkbox checked by default */
  defaultCheckedKeys?: Key[];
  /** (Controlled) Set the selected tree node */
  selectedKeys?: Key[];
  /** Tree node selected by default */
  defaultSelectedKeys?: Key[];
  selectable?: boolean;
  loadedKeys?: Key[];
  style?: React.CSSProperties;
  showIcon?: boolean;
  icon?: React.ReactNode;
  switcherIcon?: SwitcherIcon;
  prefixCls?: string;
  children?: React.ReactNode;
  blockNode?: boolean;
}
function getIcon(props: {
  isLeaf: boolean;
  expanded: boolean;
}): React.ReactNode {
  const { isLeaf, expanded } = props;
  if (isLeaf) {
    return null;
  }
  return expanded ? <Icon as={AngleDownIcon} /> : <Icon as={AngleRightIcon} />;
}
function NodeTitle({ title, icon, onSelect, disabled }: TreeNode) {
  const { disabledIconColor, disabledTextColor } = useTreeStyle();
  return (
    <Flex alignItems="center">
      {icon &&
        (disabled ? (
          <Box mr="2x" {...disabledIconColor}>
            {icon.component}
          </Box>
        ) : (
          <Box mr="2x">{icon.component}</Box>
        ))}
      {onSelect ? (
        disabled ? (
          <Text cursor="pointer" {...disabledTextColor}>
            {title}
          </Text>
        ) : (
          <Text onClick={(e) => onSelect(e)} cursor="pointer">
            {title}
          </Text>
        )
      ) : disabled ? (
        <Text {...disabledTextColor}>{title}</Text>
      ) : (
        <Text>{title}</Text>
      )}
    </Flex>
  );
}
function TreeNodeItem({
  node,
  onCheck,
  checkedKeys,
  onExpand,
  parents,
}: TreeNodeProps) {
  const [checked, setChecked] = React.useState<boolean>(
    isChecked(node.key, node.children, checkedKeys)
  );
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [expanded, setExpanded] = React.useState<boolean>(!!node.expanded);
  const [indeterminate, setIndeterminate] = React.useState<boolean>(
    isIndeterminate(node.key, node.children, checkedKeys)
  );
  // const [parents, setParents] = React.useState<Key[]>([]);
  const handleExpand = React.useCallback(async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && onExpand && node.children && !node.children.length) {
      setLoading(true);
      await onExpand([...parents, node.key]);
      setLoading(false);
    }
  }, [expanded]);
  const handleCheck = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checkStatus = e.target.checked;
      const newCheckedKeys = getCheckedKeys(
        node.key,
        node.children,
        checkStatus,
        checkedKeys
      );
      setChecked(checkStatus);
      setIndeterminate(false);
      onCheck(newCheckedKeys);
    },
    [node, checkedKeys, onCheck]
  );
  const handleChildCheck = React.useCallback(
    (childCheckedKeys: Key[]) => {
      const newIndeterminate = isIndeterminate(
        node.key,
        node.children,
        childCheckedKeys
      );
      const newChecked = node.children
        ? node.children.every((child) => childCheckedKeys.includes(child.key))
        : false;
      const newCheckedKeys = newChecked
        ? node.disableCheckbox
          ? Array.from(new Set([...childCheckedKeys]))
          : Array.from(new Set([node.key, ...childCheckedKeys]))
        : childCheckedKeys.filter((key) => key !== node.key);
      setIndeterminate(newIndeterminate);
      setChecked(newChecked);
      onCheck(newCheckedKeys);
    },
    [node, onCheck]
  );
  React.useEffect(() => {
    const newChecked = isChecked(node.key, node.children, checkedKeys);
    const newIndeterminate = isIndeterminate(
      node.key,
      node.children,
      checkedKeys
    );
    setChecked(newChecked);
    setIndeterminate(newIndeterminate);
  }, [checkedKeys]);
  return (
    <>
      <Stack direction="row" mb="8px">
        {node.children &&
          (expanded ? (
            <Icon as={AngleDownIcon} onClick={handleExpand} mr="8px" />
          ) : (
            <Icon as={AngleRightIcon} onClick={handleExpand} mr="8px" />
          ))}
        {node.disableCheckbox ? (
          <NodeTitle {...node} key={node.key} />
        ) : (
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            onChange={handleCheck}
            disabled={node.disabled || false}
          >
            <NodeTitle {...node} key={node.key} />
          </Checkbox>
        )}
      </Stack>
      {node.children && expanded && (
        <Stack
          direction="column"
          pl="6x"
          mt="1x"
          spacing="1x"
          shouldWrapChildren
        >
          {isLoading && <Spinner size="sm" />}
          {node.children.map((child, index) => (
            <TreeNodeItem
              node={child}
              onCheck={handleChildCheck}
              checkedKeys={checkedKeys}
              key={`${child.key}-${index}`}
              onExpand={onExpand}
              parents={[...parents, node.key]}
            />
          ))}
        </Stack>
      )}
    </>
  );
}

function Tree({
  treeData,
  checkedKeys = [],
  onCheck,
  style,
  onExpand,
  flatArray,
}: TreeProps) {
  useWhyDidYouUpdate('Tree', {
    treeData,
    checkedKeys,
    onCheck,
    style,
    onExpand,
  });
  const [internalCheckedKeys, setInternalCheckedKeys] =
    React.useState<Key[]>(checkedKeys);
  const handleCheck = useCallback(
    (checkedList: Key[]) => {
      setInternalCheckedKeys(checkedList);
      if (onCheck) {
        onCheck(checkedList);
      }
    },
    [onCheck]
  );
  useEffect(() => {
    // if (isEqual(checkedKeys, internalCheckedKeys)) {
    //   return;
    // }
    setInternalCheckedKeys(checkedKeys);
  }, [checkedKeys, flatArray]);
  useWhyDidYouUpdate('Tree', {
    treeData,
    checkedKeys,
    onCheck,
    style,
    onExpand,
    internalCheckedKeys,
  });
  return (
    <Scrollbar
      minHeight={100}
      maxHeight={500}
      overflow="auto"
      // resize="vertical"
    >
      <Flex direction="column" width="100%" {...style}>
        {treeData.map((node, index) => {
          return (
            <TreeNodeItem
              node={node}
              onCheck={handleCheck}
              checkedKeys={internalCheckedKeys}
              key={`${node.title}-${index}`}
              onExpand={onExpand}
              parents={[]}
            />
          );
        })}
      </Flex>
    </Scrollbar>
  );
}

export default React.memo(Tree);
